// PIREP raw → plain-text translator.
//
// A PIREP report is a slash-delimited string with up to ~14 fields.
// Format reference (FAA AC 00-45H §5.10):
//
//   <station> UA|UUA /OV <location> /TM <hhmm> /FL <level>
//        /TP <aircraft> /SK <sky cond> /WX <wx> /TA <temp C>
//        /WV <wind> /TB <turbulence> /IC <icing> /RM <remarks>
//
// Examples:
//   "LGB UA /OV KLGB/TM 1609/FL013/TP C172/SK BKN013"
//   "EEO UA /OV EKR018007/TM 1603/FL430/TP F2TH/TB CONS LGT CHOP/RM ZDVWC-04"
//   "ABQ UUA /OV TBC270020/TM 0345/FL310/TP B738/TB SEV CAT/RM DEV ND"
//
// We surface a structured object so the detail card can render each
// field as a labeled row, plus a single 'plain' string that strings
// the human-readable narrative together (good for screen readers,
// copy/paste, voice readout).

export interface ParsedPirep {
  reportType: 'PIREP' | 'AIREP' | 'URGENT_PIREP';
  station: string | null;
  /** Position relative to a fix or airport. e.g. "KLGB" or "EKR018007"
   *  (radial 018 from EKR at 7 NM). */
  over: string | null;
  /** Time in HHmm UTC. */
  timeUtc: string | null;
  /** Flight level / altitude string as reported. */
  flightLevel: string | null;
  /** Aircraft type code (C172, B738, etc.). */
  aircraftType: string | null;
  /** Sky cover string with cloud bases/tops. e.g. "BKN013" / "OVC020-040". */
  sky: string | null;
  /** Free-text wx phenomena. */
  weather: string | null;
  /** Temperature in degrees C. */
  tempC: number | null;
  /** Wind direction/speed. e.g. "27045". */
  wind: string | null;
  /** Turbulence intensity + type. e.g. "MOD CAT 280-310" */
  turbulence: string | null;
  /** Icing intensity + type. e.g. "MOD MIXED" */
  icing: string | null;
  /** Free-form remarks. */
  remarks: string | null;
  /** Original raw text. */
  raw: string;
}

const FIELD_RE = /\s*\/(OV|TM|FL|TP|SK|WX|TA|WV|TB|IC|RM)\b/g;

export function parsePirepRaw(raw: string): ParsedPirep {
  const trimmed = (raw || '').trim();

  // Header: STATION UA|UUA, then the field stream begins at the first /XX.
  const headerMatch = trimmed.match(/^(\S+)\s+(UUA|UA|AIREP)\b/i);
  const station = headerMatch ? headerMatch[1] : null;
  const reportType: ParsedPirep['reportType'] =
    headerMatch?.[2]?.toUpperCase() === 'UUA' ? 'URGENT_PIREP'
    : headerMatch?.[2]?.toUpperCase() === 'AIREP' ? 'AIREP'
    : 'PIREP';

  // Split field-by-field using the slash codes as anchors.
  const fields: Record<string, string> = {};
  // Locate every /XX anchor and slice text up to the next anchor (or end).
  const anchors: Array<{ code: string; idx: number }> = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(FIELD_RE.source, 'g');
  while ((m = re.exec(trimmed)) !== null) {
    anchors.push({ code: m[1], idx: m.index });
  }
  for (let i = 0; i < anchors.length; i++) {
    const start = anchors[i].idx + ('/' + anchors[i].code).length;
    const end = i + 1 < anchors.length ? anchors[i + 1].idx : trimmed.length;
    fields[anchors[i].code] = trimmed.slice(start, end).trim();
  }

  return {
    reportType,
    station,
    over: fields['OV'] || null,
    timeUtc: fields['TM'] || null,
    flightLevel: fields['FL'] || null,
    aircraftType: fields['TP'] || null,
    sky: fields['SK'] || null,
    weather: fields['WX'] || null,
    tempC: fields['TA'] != null ? parseTempC(fields['TA']) : null,
    wind: fields['WV'] || null,
    turbulence: fields['TB'] || null,
    icing: fields['IC'] || null,
    remarks: fields['RM'] || null,
    raw: trimmed,
  };
}

/** Parse PIREP TA value. Often signed integer in C, sometimes "M03"
 *  for -3C (M = minus). */
function parseTempC(s: string): number | null {
  const t = s.trim().toUpperCase();
  if (!t) return null;
  if (/^M\d+$/.test(t)) return -parseInt(t.slice(1), 10);
  const n = parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
}

/** Render a parsed PIREP as a human-readable narrative paragraph. */
export function plainTextPirep(p: ParsedPirep): string {
  const parts: string[] = [];

  // Lead with urgency + aircraft.
  if (p.reportType === 'URGENT_PIREP') {
    parts.push('URGENT PIREP.');
  } else if (p.reportType === 'AIREP') {
    parts.push('Air Report.');
  }
  if (p.aircraftType) parts.push(`Aircraft type ${p.aircraftType}.`);

  // Where + when + altitude.
  const wxen: string[] = [];
  if (p.over) wxen.push(`Over ${humanLocation(p.over)}`);
  if (p.timeUtc) wxen.push(`at ${p.timeUtc}Z`);
  if (p.flightLevel) wxen.push(`at ${humanFlightLevel(p.flightLevel)}`);
  if (wxen.length) parts.push(wxen.join(' ') + '.');

  // Sky.
  if (p.sky) parts.push(`Sky: ${humanSky(p.sky)}.`);
  // Wx phenomena.
  if (p.weather) parts.push(`Weather: ${p.weather}.`);
  // Temp.
  if (p.tempC != null) parts.push(`Temperature ${p.tempC}\u00b0C.`);
  // Wind.
  if (p.wind) parts.push(`Wind: ${humanWind(p.wind)}.`);
  // Turbulence.
  if (p.turbulence) parts.push(`Turbulence: ${humanTurbulence(p.turbulence)}.`);
  // Icing.
  if (p.icing) parts.push(`Icing: ${humanIcing(p.icing)}.`);
  // Remarks.
  if (p.remarks) parts.push(`Remarks: ${p.remarks}.`);

  return parts.join(' ').trim();
}

function humanLocation(over: string): string {
  // Patterns:
  //   KLGB                          - airport ident, simple
  //   EKR018007                     - radial 018, distance 007 from EKR
  //   TBC270020                     - same: 270 deg radial, 20 NM
  const m = over.match(/^([A-Z0-9]{3,5})(\d{3})(\d{2,3})$/);
  if (m) {
    const [, fix, brg, dist] = m;
    return `${fix} ${brg.replace(/^0+/, '') || '0'}\u00b0 / ${parseInt(dist, 10)} NM`;
  }
  return over;
}

function humanFlightLevel(fl: string): string {
  const t = fl.trim();
  // FL310 / 310 / DURD / UNKN / SFC
  if (/^DURD$/i.test(t)) return 'during descent';
  if (/^DURC$/i.test(t)) return 'during climb';
  if (/^UNKN$/i.test(t)) return 'altitude unknown';
  if (/^SFC$/i.test(t)) return 'surface';
  if (/^\d{3}$/.test(t)) return `FL${t}`;
  if (/^\d+$/.test(t)) return `${parseInt(t, 10) * 100} ft`;
  return t;
}

function humanSky(sky: string): string {
  // Patterns like "BKN013", "OVC020-040", "FEW040 BKN080"
  return sky.replace(/\b(SKC|CLR|FEW|SCT|BKN|OVC|OVX)(\d{3})(?:-(\d{3}))?\b/g, (_, cov, base, top) => {
    const cm: Record<string, string> = {
      SKC: 'Sky clear', CLR: 'Clear', FEW: 'Few clouds at', SCT: 'Scattered at',
      BKN: 'Broken at', OVC: 'Overcast at', OVX: 'Sky obscured (vis vert)',
    };
    const baseFt = parseInt(base, 10) * 100;
    if (cov === 'SKC' || cov === 'CLR' || cov === 'OVX') return cm[cov];
    if (top != null) {
      const topFt = parseInt(top, 10) * 100;
      return `${cm[cov]} ${baseFt.toLocaleString()} ft, tops ${topFt.toLocaleString()} ft`;
    }
    return `${cm[cov]} ${baseFt.toLocaleString()} ft`;
  });
}

function humanWind(wv: string): string {
  // 27045 -> 270 deg @ 45 kt; 27045G55 -> 270 @ 45 gusting 55
  const m = wv.match(/^(\d{3})(\d{2,3})(?:G(\d{2,3}))?$/);
  if (m) {
    const [, dir, spd, gst] = m;
    const dirN = parseInt(dir, 10);
    const dirStr = dirN === 0 ? '360' : String(dirN).padStart(3, '0');
    return gst
      ? `${dirStr}\u00b0 at ${parseInt(spd, 10)} kt gusting ${parseInt(gst, 10)} kt`
      : `${dirStr}\u00b0 at ${parseInt(spd, 10)} kt`;
  }
  return wv;
}

function humanTurbulence(tb: string): string {
  // Common forms: "LGT", "MOD CAT", "MOD-SEV CHOP", "CONS LGT CHOP", "INTMT MOD"
  const expansions: Array<[RegExp, string]> = [
    [/\bNEG\b/g, 'none reported'],
    [/\bLGT\b/g, 'light'],
    [/\bMOD\b/g, 'moderate'],
    [/\bSEV\b/g, 'severe'],
    [/\bEXTM\b/g, 'extreme'],
    [/\bCHOP\b/g, 'chop'],
    [/\bCAT\b/g, 'clear-air turbulence'],
    [/\bCONS\b/g, 'continuous'],
    [/\bINTMT\b/g, 'intermittent'],
    [/\bOCNL\b/g, 'occasional'],
  ];
  let out = tb;
  for (const [re, rep] of expansions) out = out.replace(re, rep);
  return out;
}

function humanIcing(ic: string): string {
  const expansions: Array<[RegExp, string]> = [
    [/\bNEG\b/g, 'none reported'],
    [/\bTRC\b/g, 'trace'],
    [/\bLGT\b/g, 'light'],
    [/\bMOD\b/g, 'moderate'],
    [/\bSEV\b/g, 'severe'],
    [/\bRIME\b/g, 'rime'],
    [/\bCLR\b/g, 'clear'],
    [/\bMXD\b/g, 'mixed'],
    [/\bMIXED\b/g, 'mixed'],
  ];
  let out = ic;
  for (const [re, rep] of expansions) out = out.replace(re, rep);
  return out;
}
